import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { MdCancel } from "react-icons/md";
import { CircularProgress, MenuItem, Select } from "@mui/material";
import Table from "../shared/Table";
import {
  useGetInvitationCodeQuery,
  useSetInvitationCodeMutation,
} from "../../api/auth";
import { useGetAdminsQuery, useChangeRoleMutation } from "../../api/admins";
import "./style.scss";
import { changeRoleDatas } from "../../constants/menuLists";
import { convertToCapitalize } from "../../constants/extras/convertToCapitalize";
import { useGetMetrosQuery } from "../../api/metro";
import { checkArrForMapping } from "../../constants/validaitonSchema/validation.ts";
import { sendErrorMessage } from "../../constants/extras/errorHandlers";
import { sendSuccessMessage } from "../../constants/extras/sendSuccessMsg";
import {
  Option,
  ValueContainer,
  animatedComponents,
} from "../../components/shared/options/MessageOption";
import { metroListCurrentStyles } from "./metroListCurrentStyle"
import ReactSelect from "react-select";
import { canAssignRole } from "../../auth/permissions";

const Current = () => {
  const currentUser = useSelector((state) => state.auth.currentUser);

  const [metros, setMetros] = useState({});
  const [options, setOptions] = useState([]);

  const { data: admins, isFetching } = useGetAdminsQuery({
    refetchOnMountOrArgChange: true,
  });

  const { data: AllMetros, isFetching: fetchMetros } = useGetMetrosQuery(null, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!AllMetros) return;
    setOptions(
      checkArrForMapping(AllMetros) &&
      AllMetros?.map((item, i) => {
        return {
          value: item?.name ? item?.name : "-",
          label: `${item?.name ? item?.name : ""}`,
          id: item?._id
        };
      })
    );
  }, [AllMetros]);

  const [changeRole, { isLoading }] = useChangeRoleMutation();

  const { data: invitationCode } = useGetInvitationCodeQuery({
    refetchOnMountOrArgChange: true,
  });
  const [setInvitationCode] = useSetInvitationCodeMutation();
  const [isAdminLoading, setLoading] = useState(false);

  const [roles, setRoles] = useState({});

  const form = useForm();

  useEffect(() => {
    if (invitationCode) {
      form.setValue("value", invitationCode.value);
    }
  }, [invitationCode, form]);

  useEffect(() => {
    setLoading(isFetching);
    if (!admins) return;
    setRoles(admins.reduce((result, admin) => {
      result[admin._id] = admin.role;
      return result;
    }, {}));
    setMetros(admins.reduce((result, admin) => {
      result[admin._id] = admin.metros || [];
      return result;
    }, {}));
  }, [admins, isFetching]);

  const onSubmit = async (data) => setInvitationCode(data);

  const handleAssignedRole = async (item) => {
    const selectedRole = roles[item._id] || item.role;
    const selectedMetros = metros[item._id] || [];
    const isMetros = selectedMetros.length !== (item?.metros || []).length;
    const isMetroScopedRole = ["metro_manager", "dispatcher"].includes(selectedRole);
    if (isMetroScopedRole && !selectedMetros.length) {
      sendErrorMessage(`Please add Metros`)
      return
    }
    if (
      item.role !== selectedRole ||
      (isMetroScopedRole && isMetros)
    ) {
      // Existing metros come from the API with `_id`; newly selected metros
      // are stored with `id`. Support both shapes when saving a role change.
      const metroData = selectedMetros
        .map((data) => data.id || data._id)
        .filter(Boolean);
      let response = await changeRole({
        id: item._id,
        role: selectedRole,
        metros: isMetroScopedRole ? metroData : [],
      });
      if (response && response.data) {
        setRoles((currentRoles) => ({ ...currentRoles, [item._id]: response.data.role }));
        sendSuccessMessage(`${response.data.role} role assigned successfully`)
      } else {
        sendErrorMessage(`Something went wrong`)
      }
    }
    // setMetros([]);
  };

  const columns = [
    { name: "username", label: "Username", sortable: true },
    {
      name: "role",
      label: "Role",
      sortable: true,
      className: "w-25",
      renderer: (item) => (
        <span>{convertToCapitalize(item && item.role ? item.role : "")}</span>
      ),
    },
    {
      name: "change_role",
      className: "table__td_have-btn w-25 align-center",
      renderer: (item, index) => {
        if (
          item &&
          (item?._id === currentUser?._id ||
            !canAssignRole(currentUser?.role, item.role))
        ) {
          return null;
        } else {
          return (
            <React.Fragment>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Select
                  style={{ height: "40px", width: ["metro_manager", "dispatcher"].includes(roles[item._id]) ? "300px" : "240px", fontSize: "15px" }}
                  value={roles[item._id] || item.role}
                  onChange={(e) => {
                    setRoles((currentRoles) => ({
                      ...currentRoles,
                      [item._id]: e.target.value,
                    }));
                  }}
                >
                  {changeRoleDatas
                    .filter(({ value }) => canAssignRole(currentUser?.role, value))
                    .map((changeRoleData, idx) => {
                    return (
                      <MenuItem value={changeRoleData.value} key={`${idx}`} style={{ fontSize: "15px" }}>
                        {changeRoleData.title}
                      </MenuItem>
                    );
                  })}
                </Select>
                {["metro_manager", "dispatcher"].includes(roles[item._id]) && (
                  <div id="scrollContainer" >
                    <ReactSelect
                      placeholder={"Select Metro"}
                      isClearable
                      options={
                        options && Array.isArray(options) && options?.length > 0
                          ? options
                          : []
                      }
                      value={(metros[item._id] || []).map((elem) => {
                        return {
                          value: elem?.name ? elem?.name : "-",
                          label: elem?.name ? elem?.name : "",
                          id: elem._id
                        }
                      })}
                      onChange={(value) => {
                        const data = value?.map((data) => {
                          return {
                            value: data?.value ? data?.value : "-",
                            name: data?.label ? data?.label : "",
                            id: data.id
                          }
                        })
                        setMetros((currentMetros) => ({
                          ...currentMetros,
                          [item._id]: data || [],
                        }));
                      }}
                      isMulti={true}
                      allowSelectAll={true}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      styles={metroListCurrentStyles}
                      menuPortalTarget={document.body}
                      menuShouldBlockScroll={true}
                      maxMenuHeight={200}
                      menuPlacement='top'
                      components={{
                        Option,
                        ValueContainer,
                        animatedComponents,
                        IndicatorSeparator: () => null,
                      }}
                    ></ReactSelect>
                  </div>
                )}
                <button
                  className="btn btn_accent w-25 ml-auto"
                  type="button"
                  onClick={() => {
                    handleAssignedRole(item);
                  }}
                  disabled={isLoading}
                  style={{
                    minHeight: "0rem",
                    height: "2.3rem",
                    display: "block",
                    // margin: "auto",
                    fontSize: "90%",
                    maxWidth: "7rem",
                  }}
                >
                  Save
                </button>
              </div>
            </React.Fragment>
          );
        }
      },
    },
    {
      name: "remove_rights",
      className: "table__td_have-btn align-center",
      renderer: (item, index) =>
        currentUser &&
        currentUser._id !== item._id &&
        canAssignRole(currentUser.role, item.role) && (
          <button
            className="btn btn_block customized-details-btn"
            onClick={() => {
              changeRole({ id: item._id, role: "user" })
              setRoles((currentRoles) => {
                const nextRoles = { ...currentRoles };
                delete nextRoles[item._id];
                return nextRoles;
              })
            }}
          >
            Remove Admin Rights
            <MdCancel className="btn__icon btn__icon_right btn__icon_danger" />
          </button>
        ),
    },
  ];

  return (
    <React.Fragment>
      <div className="screen-users__form-wrapper" style={{ zIndex: 0 }}>
        <div className="container container_narrow screen-users__container mainTabBackground mainTabWidth">
          <div className="screen-users__form-body">
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="form__item">
                <label className="form__label">Invitation Code</label>
                <div className="form__input-group">
                  <input
                    className={`form__input ${form.formState.errors.password
                      ? "form__input_invalid"
                      : null
                      }`}
                    type="text"
                    {...form.register("value", { required: true })}
                  />
                  <button type="submit" className="btn btn_accent">
                    Save
                  </button>
                </div>
                {form.formState.errors.password && (
                  <div className="form__msg form__msg_invalid">
                    This field is required
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      <div
        className="container-fluid"
        style={{
          maxWidth: "91%",
        }}
      >
        <div className="card">
          {isAdminLoading || fetchMetros ? (
            <div className="user_loading">
              <CircularProgress className="loading-progress" />
            </div>
          ) : admins && admins.length ? (
            <div
              className="table-wrapper"
              style={{ height: "60vh", overflow: "auto", zIndex: "1" }}
            >
              <Table
                columns={columns}
                items={admins}
                className="table_users table_users_current"
              />
            </div>
          ) : (
            <div className="no_results">No Results</div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default Current;
